import React from "react";
import ReactDOM from "react-dom/client";
// Font inclusi nel bundle: l'app resta uguale anche senza internet.
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/plus-jakarta-sans/500.css";
import "@fontsource/plus-jakarta-sans/600.css";
import "@fontsource/plus-jakarta-sans/700.css";
import "@fontsource/plus-jakarta-sans/800.css";
import "./index.css";
import App from "./App";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import packageInfo from "../package.json";
import { registerQueryClient } from "./core/mutationInvalidator";
import { QUERY_CACHE_KEY } from "./core/storageKeys";
import { GOOGLE_CLIENT_ID } from "./config/app";

/**
 * React Query come su gestionale-gls: dati sempre "freschi" (staleTime
 * Infinity) e riletti solo quando qualcosa li invalida - una mutazione
 * locale (mutationInvalidator) o un evento dal server (useRealtimeSync).
 * Una query invalidata mentre la pagina non è montata si rilegge appena ci
 * si torna (refetchOnMount resta attivo per le query invalidate).
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
      gcTime: 24 * 60 * 60 * 1000,
    },
    mutations: { retry: 0 },
  },
});
registerQueryClient(queryClient);

// Cache persistita su localStorage per riaprire l'app all'istante. Si
// persistono SOLO dati leggeri e poco sensibili: dipendenti, documenti,
// presenze e importazioni (codici fiscali, IBAN, certificati) restano in
// memoria e si rileggono all'apertura, così un PC condiviso non conserva
// dati personali dei lavoratori nel browser.
const PERSISTED = new Set(["catalogo", "clienti", "clientiElenco", "piano", "panoramica", "notifiche", "people"]);
const persister = createSyncStoragePersister({
  storage: typeof window !== "undefined" ? window.localStorage : undefined,
  key: QUERY_CACHE_KEY,
  throttleTime: 1000,
});

// La rotellina non deve cambiare i numeri negli input numerici.
document.addEventListener("wheel", () => {
  if (document.activeElement && document.activeElement.type === "number") document.activeElement.blur();
});

const app = (
  <PersistQueryClientProvider
    client={queryClient}
    persistOptions={{
      persister,
      maxAge: 24 * 60 * 60 * 1000,
      buster: packageInfo.version,
      dehydrateOptions: {
        shouldDehydrateQuery: (q) => q.state.status === "success" && PERSISTED.has(q.queryKey[0]),
      },
    }}
  >
    <App />
  </PersistQueryClientProvider>
);

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>{GOOGLE_CLIENT_ID ? <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>{app}</GoogleOAuthProvider> : app}</React.StrictMode>,
);
